/**
 * GovernanceDeadlines.tsx
 * Admin/Owner dashboard for upcoming deadlines:
 * - Status chips: PLANNED / DUE / LATE / COMPLETED
 * - Assign user, Snooze (shift due_date), Mark completed
 * - iCal download for a single item or all
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calendar as CalIcon, Check, Clock, RefreshCw, Download } from 'lucide-react';
import { toast } from 'sonner';

interface DeadlineType {
  display_name: string;
  lead_days: number;
}

interface Deadline {
  id: string;
  type_id: string;
  due_date: string;
  status: string;
  notes: string | null;
  assigned_to: string | null;
  gov_deadline_types: DeadlineType;
}

function toICS(name: string, due: string, desc: string) {
  const dt = due.replace(/-/g, '');
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//CapLedger//Governance//EN',
    'BEGIN:VEVENT',
    `DTSTART;VALUE=DATE:${dt}`,
    `DTEND;VALUE=DATE:${dt}`,
    `SUMMARY:${name}`,
    `DESCRIPTION:${desc}`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

export default function GovernanceDeadlines({ companyId }: { companyId: string }) {
  const [rows, setRows] = useState<Deadline[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('gov_deadlines')
        .select(
          'id, type_id, due_date, status, notes, assigned_to, gov_deadline_types!inner(display_name, lead_days)'
        )
        .eq('company_id', companyId)
        .neq('status', 'CANCELLED')
        .order('due_date', { ascending: true });

      if (error) throw error;
      setRows(data || []);
    } catch (err) {
      console.error('Error loading governance deadlines:', err);
      toast.error('Failed to load governance deadlines');
    } finally {
      setLoading(false);
    }
  }

  async function markDone(id: string) {
    try {
      const { error } = await supabase
        .from('gov_deadlines')
        .update({
          status: 'COMPLETED',
          completed_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      toast.success('Deadline marked as completed');
      await load();
    } catch (err) {
      console.error('Error marking deadline as done:', err);
      toast.error('Failed to mark deadline as completed');
    }
  }

  async function snooze(id: string, days: number) {
    try {
      const row = rows.find((r) => r.id === id);
      if (!row) return;

      const currentDate = new Date(row.due_date);
      currentDate.setDate(currentDate.getDate() + days);

      const { error } = await supabase
        .from('gov_deadlines')
        .update({
          due_date: currentDate.toISOString().slice(0, 10),
        })
        .eq('id', id);

      if (error) throw error;

      toast.success(`Deadline snoozed by ${days} days`);
      await load();
    } catch (err) {
      console.error('Error snoozing deadline:', err);
      toast.error('Failed to snooze deadline');
    }
  }

  function downloadICS(row: Deadline) {
    try {
      const ics = toICS(
        row.gov_deadline_types.display_name,
        row.due_date,
        row.notes || 'Governance deadline reminder'
      );
      const blob = new Blob([ics], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${row.gov_deadline_types.display_name}-${row.due_date}.ics`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Calendar event downloaded');
    } catch (err) {
      console.error('Error downloading ICS:', err);
      toast.error('Failed to download calendar event');
    }
  }

  function downloadAllICS() {
    try {
      const events = rows
        .filter((r) => r.status !== 'COMPLETED')
        .map((r) => {
          const dt = r.due_date.replace(/-/g, '');
          return [
            'BEGIN:VEVENT',
            `DTSTART;VALUE=DATE:${dt}`,
            `DTEND;VALUE=DATE:${dt}`,
            `SUMMARY:${r.gov_deadline_types.display_name}`,
            `DESCRIPTION:${r.notes || 'Governance deadline reminder'}`,
            'STATUS:CONFIRMED',
            'END:VEVENT',
          ].join('\r\n');
        })
        .join('\r\n');

      const ics = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//CapLedger//Governance//EN',
        events,
        'END:VCALENDAR',
      ].join('\r\n');

      const blob = new Blob([ics], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `governance-deadlines-${new Date().toISOString().slice(0, 10)}.ics`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('All deadlines exported to calendar');
    } catch (err) {
      console.error('Error downloading all ICS:', err);
      toast.error('Failed to export calendar events');
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return (
          <Badge variant="default" className="bg-green-500 hover:bg-green-600">
            Completed
          </Badge>
        );
      case 'LATE':
        return <Badge variant="destructive">Late</Badge>;
      case 'DUE':
        return (
          <Badge variant="default" className="bg-yellow-500 hover:bg-yellow-600">
            Due
          </Badge>
        );
      case 'PLANNED':
        return <Badge variant="secondary">Planned</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  useEffect(() => {
    if (companyId) {
      load();

      // Subscribe to real-time updates
      const channel = supabase
        .channel('gov_deadlines')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'gov_deadlines',
            filter: `company_id=eq.${companyId}`,
          },
          () => {
            load();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [companyId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg font-semibold">Governance Deadlines</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Track and manage statutory compliance deadlines
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={downloadAllICS} disabled={rows.length === 0}>
            <Download className="h-4 w-4 mr-1" /> Export All
          </Button>
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            No governance deadlines scheduled
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">
                    {row.gov_deadline_types.display_name}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>{new Date(row.due_date).toLocaleDateString()}</span>
                      <span className="text-xs text-muted-foreground">
                        {getDaysUntilDue(row.due_date)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(row.status)}</TableCell>
                  <TableCell className="max-w-md">
                    {row.notes || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadICS(row)}
                        title="Download calendar event"
                      >
                        <CalIcon className="h-4 w-4" />
                      </Button>
                      {row.status !== 'COMPLETED' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => snooze(row.id, 7)}
                            title="Snooze 7 days"
                          >
                            <Clock className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => markDone(row.id)}
                            title="Mark as completed"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
